import { isString } from '@tunasong/models'
import type { SearchSummary } from '@tunasong/models'
import type { Entity, Persisted } from '@tunasong/schemas'
import { TunaDialog, getEntityPath, useNavigateToEntity, useTunaMediaQuery } from '@tunasong/ui-lib'
import cn from 'classnames'
import { useCallback, useState } from 'react'
import styles from './search-dialog.hook.module.css'
import { FullSearch } from './full-search.js'

/**
 * Search (#2555; Joni's canvas https://claude.ai/artifact/5347cecuSMm2xyYu6S3p4B). Desktop: a 640 px dialog near the
 * top; Esc (on an empty query) and the backdrop close it. Phone: full screen, with Cancel beside the input, so the
 * keyboard never covers the results. The "All Songs" tab is gone: the type chips do its job.
 */
export const useSearchDialog = (currentEntity?: Persisted<Entity>) => {
  const [open, setOpen] = useState(false)
  const phone = useTunaMediaQuery('(max-width: 599.95px)')

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])
  const navigateToEntity = useNavigateToEntity()

  const handleOpen = useCallback(
    (result: SearchSummary | Persisted<Entity> | string | null) => {
      if (!result || isString(result)) {
        return
      }
      navigateToEntity(result, { layout: 'default' })
      setOpen(false)
    },
    [navigateToEntity]
  )

  /** Cmd or Ctrl+Enter: the result in a new tab, and search stays where it was */
  const handleOpenInNewTab = (result: SearchSummary) => {
    window.open(getEntityPath({ entity: result }), '_blank', 'noopener')
  }

  const dialog = open ? (
    <TunaDialog
      open={open}
      className={cn(styles.dialog, { [styles.phone]: phone })}
      onClose={handleClose}
      maxWidth={false}
      fullScreen={phone}
      aria-label="Search"
    >
      <FullSearch
        onOpen={handleOpen}
        onOpenInNewTab={handleOpenInNewTab}
        onCancel={phone ? handleClose : undefined}
        onClose={handleClose}
        currentEntity={currentEntity}
      />
    </TunaDialog>
  ) : null

  return {
    open,
    setOpen,
    dialog,
  }
}
